import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem, Grid, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";

const userDisplay = (u) => (u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username : "—");
const PRESENTER_MENTOR = 1;
const PRESENTER_STUDENT = 2;

export default function SeminarFormDialog({ open, onClose, seminar, users, academics = [], onSave, saving }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    title: "", seminar_date: "", end_date: "", location: "", organizer: "", description: "",
    presenter_type: "", presenter_user: "", presenter_academic: "",
  });

  useEffect(() => {
    if (seminar) {
      setForm({
        title: seminar.title || "",
        seminar_date: seminar.seminar_date ? seminar.seminar_date.slice(0, 10) : "",
        end_date: seminar.end_date ? seminar.end_date.slice(0, 10) : "",
        location: seminar.location || "",
        organizer: seminar.organizer || "",
        description: seminar.description || "",
        presenter_type: seminar.presenter_type != null ? String(seminar.presenter_type) : "",
        presenter_user: seminar.presenter_user ?? "",
        presenter_academic: seminar.presenter_academic ?? "",
      });
    } else {
      setForm({
        title: "", seminar_date: "", end_date: "", location: "", organizer: "", description: "",
        presenter_type: "", presenter_user: "", presenter_academic: "",
      });
    }
  }, [seminar, open]);

  const handleChange = (e) => { const { name, value } = e.target; setForm((prev) => ({ ...prev, [name]: value })); };
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      title: form.title,
      seminar_date: form.seminar_date || null,
      end_date: form.end_date || null,
      location: form.location || "",
      organizer: form.organizer ? Number(form.organizer) : null,
      description: form.description || "",
      presenter_type: form.presenter_type ? Number(form.presenter_type) : null,
      presenter_user: form.presenter_type === String(PRESENTER_MENTOR) && form.presenter_user ? Number(form.presenter_user) : null,
      presenter_academic: form.presenter_type === String(PRESENTER_STUDENT) && form.presenter_academic ? Number(form.presenter_academic) : null,
    });
  };

  const userList = users?.results || users || [];
  const academicList = academics?.results ?? academics ?? [];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{seminar ? t("academic.editSeminar", "Edit Seminar") : t("academic.addSeminar", "Add Seminar")}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}><TextField fullWidth required name="title" label={t("academic.seminarTitle", "Title")} value={form.title} onChange={handleChange} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth required name="seminar_date" type="date" label={t("academic.startDate", "Start date")} value={form.seminar_date} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth name="end_date" type="date" label={t("academic.endDateMultiDay", "End date (multi-day)")} value={form.end_date} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" placeholder={t("academic.leaveBlankSingleDay", "Leave blank for single day")} /></Grid>
            <Grid item xs={12}><TextField fullWidth name="location" label={t("academic.location", "Location")} value={form.location} onChange={handleChange} size="small" /></Grid>
            <Grid item xs={12}><TextField fullWidth name="organizer" select label={t("academic.organizer", "Organizer")} value={form.organizer} onChange={handleChange} size="small"><MenuItem value="">—</MenuItem>{userList.map((u) => <MenuItem key={u.id} value={u.id}>{userDisplay(u)}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12}><TextField fullWidth name="presenter_type" select label={t("academic.presenterType", "Presenter")} value={form.presenter_type} onChange={handleChange} size="small"><MenuItem value="">—</MenuItem><MenuItem value={String(PRESENTER_MENTOR)}>{t("academic.mentor", "Mentor")}</MenuItem><MenuItem value={String(PRESENTER_STUDENT)}>{t("academic.student", "Student")}</MenuItem></TextField></Grid>
            {form.presenter_type === String(PRESENTER_MENTOR) && (
              <Grid item xs={12}><TextField fullWidth name="presenter_user" select label={t("academic.presenterMentor", "Presenter (mentor)")} value={form.presenter_user} onChange={handleChange} size="small"><MenuItem value="">—</MenuItem>{userList.map((u) => <MenuItem key={u.id} value={u.id}>{userDisplay(u)}</MenuItem>)}</TextField></Grid>
            )}
            {form.presenter_type === String(PRESENTER_STUDENT) && (
              <Grid item xs={12}><TextField fullWidth name="presenter_academic" select label={t("academic.presenterStudent", "Presenter (student)")} value={form.presenter_academic} onChange={handleChange} size="small"><MenuItem value="">—</MenuItem>{academicList.map((a) => <MenuItem key={a.id} value={a.id}>{a.name_display || `${a.first_name || ""} ${a.last_name || ""}`.trim() || `#${a.id}`}</MenuItem>)}</TextField></Grid>
            )}
            <Grid item xs={12}><TextField fullWidth name="description" label={t("academic.description", "Description")} value={form.description} onChange={handleChange} multiline rows={2} size="small" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>{t("common.cancel", "Cancel")}</Button><Button type="submit" variant="contained" disabled={saving || !form.title || !form.seminar_date}>{saving ? <CircularProgress size={24} /> : t("common.save", "Save")}</Button></DialogActions>
      </form>
    </Dialog>
  );
}
